// ========================================
// PAGE HOME - VERSION PREMIUM
// Même style visuel que la page About
// Animations spectaculaires + Données Firebase
// ========================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../../components/common/Navigation';
import Footer from '../../components/common/Footer';
import { useHomePage, useSiteSettings, useProjects } from '../../hooks';
import { 
  FiArrowRight, 
  FiMail, 
  FiExternalLink, 
  FiCheck, 
  FiChevronDown,
  FiDownload,
  FiMapPin,
  FiAward,
  FiZap,
  FiTrendingUp,
  FiMessageCircle,
  FiSend,
  FiPhone,
  FiBook,
  FiBriefcase,
  FiUsers,
  FiEdit3,
  FiGlobe,
  FiTarget,
  FiHeart,
  FiStar,
  FiFileText,
  FiShare2,
  FiMic,
  FiCamera,
  FiVideo,
  FiCode,
  FiMusic,
  FiCalendar,
  FiClock,
  FiShield,
  FiSearch,
  FiSettings,
  FiLayout,
  FiPenTool,
  FiMonitor,
  FiSmartphone,
  FiDatabase,
  FiCloud,
  FiCpu,
  FiPackage,
  FiLayers,
  FiCompass,
  FiCoffee,
  FiFeather,
  FiGift,
  FiHeadphones,
  FiHome,
  FiImage,
  FiInfo,
  FiKey,
  FiLink,
  FiList,
  FiLock,
  FiLogIn,
  FiMap,
  FiMenu,
  FiMoon,
  FiMoreHorizontal,
  FiMousePointer,
  FiMove,
  FiNavigation,
  FiPaperclip,
  FiPercent,
  FiPieChart,
  FiPlay,
  FiPlus,
  FiPocket,
  FiPower,
  FiPrinter,
  FiRadio,
  FiRefreshCw,
  FiRepeat,
  FiRotateCw,
  FiRss,
  FiSave,
  FiScissors,
  FiServer,
  FiSidebar,
  FiSliders,
  FiSpeaker,
  FiSquare,
  FiSun,
  FiSunrise,
  FiSunset,
  FiTablet,
  FiTag,
  FiTerminal,
  FiThumbsUp,
  FiToggleLeft,
  FiTool,
  FiTrash2,
  FiTruck,
  FiTv,
  FiType,
  FiUmbrella,
  FiUnderline,
  FiUnlock,
  FiUpload,
  FiUser,
  FiUserCheck,
  FiUserPlus,
  FiVoicemail,
  FiVolume2,
  FiWatch,
  FiWifi,
  FiWind,
  FiXCircle,
  FiZoomIn
} from 'react-icons/fi';
import { 
  FaLinkedin, 
  FaTwitter, 
  FaFacebook, 
  FaInstagram, 
  FaWhatsapp, 
  FaYoutube, 
  FaTiktok 
} from 'react-icons/fa';
import './Home.css';

// ============================================
// MAPPING DES ICÔNES - COMPLET
// ============================================

const allIconsMap = {
  // Documents & Écriture
  cv: FiFileText,
  file: FiFileText,
  filetext: FiFileText,
  pen: FiEdit3,
  edit: FiEdit3,
  pencil: FiEdit3,
  penTool: FiPenTool,
  feather: FiFeather,
  type: FiType,
  
  // Communication
  share: FiShare2,
  mic: FiMic,
  microphone: FiMic,
  camera: FiCamera,
  video: FiVideo,
  megaphone: FiMessageCircle,
  message: FiMessageCircle,
  chat: FiMessageCircle,
  mail: FiMail,
  email: FiMail,
  phone: FiPhone,
  voicemail: FiVoicemail,
  speaker: FiSpeaker,
  volume: FiVolume2,
  headphones: FiHeadphones,
  radio: FiRadio,
  rss: FiRss,
  
  // Médias
  music: FiMusic,
  image: FiImage,
  play: FiPlay,
  tv: FiTv,
  
  // Formation & Éducation
  book: FiBook,
  users: FiUsers,
  user: FiUser,
  usercheck: FiUserCheck,
  userplus: FiUserPlus,
  formation: FiUsers,
  
  // Qualité & Récompenses
  star: FiStar,
  award: FiAward,
  gift: FiGift,
  thumbsup: FiThumbsUp,
  
  // Émotions & Valeurs
  heart: FiHeart,
  coffee: FiCoffee,
  
  // Objectifs & Stratégie
  target: FiTarget,
  compass: FiCompass,
  navigation: FiNavigation,
  map: FiMap,
  
  // Business & Travail
  briefcase: FiBriefcase,
  consulting: FiBriefcase,
  
  // International & Web
  globe: FiGlobe,
  digital: FiGlobe,
  link: FiLink,
  externallink: FiExternalLink,
  
  // Innovation & Énergie
  lightbulb: FiZap,
  zap: FiZap,
  power: FiPower,
  
  // Croissance & Stats
  rocket: FiTrendingUp,
  trending: FiTrendingUp,
  chart: FiPieChart,
  piechart: FiPieChart,
  percent: FiPercent,
  
  // Technologie
  code: FiCode,
  terminal: FiTerminal,
  cpu: FiCpu,
  server: FiServer,
  database: FiDatabase,
  cloud: FiCloud,
  
  // Design & Création
  palette: FiPenTool,
  layers: FiLayers,
  layout: FiLayout,
  grid: FiMenu,
  sidebar: FiSidebar,
  sliders: FiSliders,
  
  // Temps & Planning
  calendar: FiCalendar,
  clock: FiClock,
  watch: FiWatch,
  sunrise: FiSunrise,
  sunset: FiSunset,
  
  // Sécurité
  shield: FiShield,
  lock: FiLock,
  unlock: FiUnlock,
  key: FiKey,
  
  // Recherche & Analyse
  search: FiSearch,
  zoomin: FiZoomIn,
  
  // Paramètres & Outils
  settings: FiSettings,
  tool: FiTool,
  scissors: FiScissors,
  
  // Actions
  save: FiSave,
  upload: FiUpload,
  download: FiDownload,
  refresh: FiRefreshCw,
  repeat: FiRepeat,
  rotate: FiRotateCw,
  move: FiMove,
  
  // Navigation
  home: FiHome,
  menu: FiMenu,
  list: FiList,
  plus: FiPlus,
  
  // Appareils
  monitor: FiMonitor,
  smartphone: FiSmartphone,
  tablet: FiTablet,
  printer: FiPrinter,
  
  // Météo & Nature
  sun: FiSun,
  moon: FiMoon,
  umbrella: FiUmbrella,
  wind: FiWind,
  
  // Divers
  package: FiPackage,
  truck: FiTruck,
  tag: FiTag,
  paperclip: FiPaperclip,
  info: FiInfo,
  wifi: FiWifi,
  pocket: FiPocket,
  mousepointer: FiMousePointer,
  toggle: FiToggleLeft,
  square: FiSquare,
  login: FiLogIn,
  more: FiMoreHorizontal,
  underline: FiUnderline,
  xcircle: FiXCircle,
  
  // Compatibilité anciennes valeurs
  communication: FiMessageCircle,
  redaction: FiEdit3,
  strategie: FiTarget,
  handshake: FiUsers
};

// Fonction pour récupérer une icône
const getIcon = (iconName) => {
  if (!iconName) return <FiStar />;
  const normalizedName = iconName.toLowerCase().replace(/[-_\s]/g, '');
  const Icon = allIconsMap[normalizedName] || FiStar;
  return <Icon />;
};

// Fonction pour les services (alias)
const getServiceIcon = (iconName) => getIcon(iconName);


// ============================================
// FONCTION DE NETTOYAGE DU CONTENU HTML
// ============================================
const cleanHtmlContent = (html) => {
  if (!html) return '';
  
  let cleaned = html;
  // Remplacer les espaces insécables
  cleaned = cleaned.replace(/&nbsp;/g, ' ');
  cleaned = cleaned.replace(/\u00A0/g, ' ');
  // Supprimer les espaces multiples
  cleaned = cleaned.replace(/  +/g, ' ');
  // Supprimer le justify qui cause des problèmes
  cleaned = cleaned.replace(/ql-align-justify/g, 'ql-align-left');
  cleaned = cleaned.replace(/style\s*=\s*["'][^"']*text-align\s*:\s*justify[^"']*["']/gi, '');
  // Supprimer white-space: nowrap qui empêche le retour à la ligne
  cleaned = cleaned.replace(/white-space\s*:\s*nowrap/gi, 'white-space: normal');
  
  return cleaned;
};

// ============================================
// COMPOSANT TYPING EFFECT - CORRIGÉ MOBILE
// ============================================
const TypingEffect = ({ roles, isLoading }) => {
  const [displayText, setDisplayText] = useState('');
  const [cursorState, setCursorState] = useState('typing'); // 'typing' | 'blinking'
  const [roleIndex, setRoleIndex] = useState(0);
  
  const stateRef = useRef({
    charIndex: 0,
    isDeleting: false,
    isPaused: false
  });
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!roles || roles.length === 0 || isLoading) {
      return;
    }

    const currentRole = roles[roleIndex];
    
    // Reset de l'état pour ce nouveau rôle
    stateRef.current = {
      charIndex: 0,
      isDeleting: false,
      isPaused: false
    };
    setDisplayText('');
    setCursorState('typing');

    const type = () => {
      if (!isMountedRef.current) return;
      
      const state = stateRef.current;
      
      // Mode frappe
      if (!state.isDeleting && !state.isPaused) {
        if (state.charIndex < currentRole.length) {
          state.charIndex++;
          setDisplayText(currentRole.substring(0, state.charIndex));
          setCursorState('typing');
          timeoutRef.current = setTimeout(type, 100);
        } else {
          // Frappe terminée, pause avant effacement
          state.isPaused = true;
          setCursorState('blinking');
          timeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            state.isPaused = false;
            state.isDeleting = true;
            setCursorState('typing');
            type();
          }, 2000);
        }
      }
      // Mode effacement
      else if (state.isDeleting) {
        if (state.charIndex > 0) {
          state.charIndex--;
          setDisplayText(currentRole.substring(0, state.charIndex));
          setCursorState('typing');
          timeoutRef.current = setTimeout(type, 50);
        } else {
          // Effacement terminé, passer au rôle suivant
          setCursorState('blinking');
          timeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) return;
            setRoleIndex((prev) => (prev + 1) % roles.length);
          }, 400);
        }
      }
    };

    // Démarrer avec un petit délai
    timeoutRef.current = setTimeout(type, 600);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [roleIndex, roles, isLoading]);

  return (
    <motion.div 
      className="profile-title"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 0.6 }}
    >
      <span className="role-prefix">Je suis </span>
      <span className="role-dynamic">{displayText}</span>
      <span className={`typing-cursor ${cursorState}`}>|</span>
    </motion.div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
const Home = () => {
  // ========== STATE ==========
  const [initialLoading, setInitialLoading] = useState(true);
  
  // ========== REFS ==========
  const heroRef = useRef(null);
  
  // ========== HOOKS FIREBASE ==========
  const { content: homeContent, loading: homeLoading } = useHomePage();
  const { settings, loading: settingsLoading } = useSiteSettings();
  const { projects: allProjects, loading: projectsLoading } = useProjects();

  // ========== SCROLL ANIMATIONS ==========
  const { scrollYProgress: heroScrollProgress } = useScroll({
    offset: ["start start", "end start"]
  });

  const heroY = useTransform(heroScrollProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(heroScrollProgress, [0, 1], [1, 0.3]);

  // ========== LOADING EFFECT ==========
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // ========== DONNÉES ==========
  const isLoading = homeLoading || settingsLoading || projectsLoading || initialLoading;

  const hero = homeContent?.hero || {};
  const introduction = homeContent?.introduction || {};
  const sypnase = homeContent?.sypnase || {};
  const services = homeContent?.services || [];
  const featuredProjectIds = homeContent?.featuredProjects || [];

  // Projets mis en avant
  const featuredProjects = useMemo(() => {
    if (!allProjects?.length) return [];
    
    if (featuredProjectIds.length > 0) {
      const featured = featuredProjectIds
        .map(id => allProjects.find(p => p.id === id))
        .filter(Boolean);
      
      if (featured.length > 0) return featured.slice(0, 3);
    }
    
    return allProjects.filter(p => p.isFeatured).slice(0, 3);
  }, [allProjects, featuredProjectIds]);

  // Rôles pour l'effet typing
  const roles = useMemo(() => {
    if (hero.roles && Array.isArray(hero.roles) && hero.roles.length > 0) {
      return hero.roles;
    }
    return ['Communicant', 'Écrivain', 'Leader Associatif'];
  }, [hero.roles]);

  // Données de base
  const name = hero.title || settings?.siteName || 'Gustave KONATE';
  const firstName = name.split(' ')[0];
  const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const shortBio = hero.slogan || "Communiquer pour transformer, écrire pour inspirer, former pour l'avenir.";
  const location = settings?.address || 'Ouagadougou, Burkina Faso';
  const socialLinks = settings?.socialLinks || {};

  // Highlights
  const highlights = hero.highlights || [
    { icon: 'zap', label: 'Créativité' },
    { icon: 'trending', label: 'Innovation' },
    { icon: 'award', label: 'Excellence' }
  ];

  // Stats
  const stats = hero.quickStats || [
    { number: '4+', label: "Années d'expérience" },
    { number: '50+', label: 'Projets réalisés' },
    { number: '100%', label: 'Engagement' }
  ];

  // ========== SCROLL FUNCTION ==========
  const scrollToSection = useCallback((sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // ========== HELPERS ==========
  const getWhatsAppLink = () => {
    const phone = settings?.phones?.[0] || socialLinks?.whatsapp || '';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    return `https://wa.me/${cleanPhone}?text=Bonjour ${firstName}, je suis intéressé par vos services !`;
  };

  // ========== VARIANTS D'ANIMATION ==========
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", damping: 20, stiffness: 100 }
    }
  };

  // ============================================
  // LOADING SCREEN SPECTACULAIRE
  // ============================================
  if (isLoading) {
    return (
      <div className="home-page">
        <div className="loading-screen">
          <motion.div 
            className="loading-avatar"
            animate={{ 
              scale: [0.8, 1, 0.8], 
              borderRadius: ["40%", "50%", "40%"],
              boxShadow: [
                "0 0 0 0 rgba(3, 35, 107, 0.4)",
                "0 0 0 20px transparent",
                "0 0 0 0 rgba(3, 35, 107, 0.4)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {hero.profileImage ? (
              <img src={hero.profileImage} alt={name} />
            ) : (
              <span>{initials}</span>
            )}
          </motion.div>
          <motion.div className="loading-particles">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="loading-particle"
                animate={{
                  y: [-20, 20, -20],
                  x: [Math.sin(i) * 30, Math.cos(i) * 30, Math.sin(i) * 30],
                  opacity: [0.3, 1, 0.3]
                }}
                transition={{ duration: 2, delay: i * 0.2, repeat: Infinity }}
              />
            ))}
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="loading-text"
          >
            Chargement du portfolio
          </motion.p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDU PRINCIPAL
  // ============================================
  return (
    <div className="home-page">
      <Navigation />

      {/* ========== HERO SECTION ========== */}
      <section className="home-hero" ref={heroRef}>
        <motion.div className="hero-background" style={{ y: heroY }}>
          {/* Particules animées */}
          <div className="particle-network">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
            <div className="particle particle-6"></div>
            <div className="orbit orbit-1"></div>
            <div className="orbit orbit-2"></div>
            <div className="orbit orbit-3"></div>
          </div>
          <div className="hero-gradient"></div>
        </motion.div>
        
        <motion.div className="hero-content" style={{ opacity: heroOpacity }}>
          <div className="home-container">
            <motion.div
              className="profile-section"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Image de profil */}
              <motion.div className="profile-image-wrapper" variants={itemVariants}>
                <div className="image-outline"></div>
                <div className="image-glow"></div>
                <div className="image-container">
                  {hero.profileImage ? (
                    <img
                      src={hero.profileImage}
                      alt={name}
                      className="profile-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="profile-placeholder" style={{ display: hero.profileImage ? 'none' : 'flex' }}>
                    <span>{initials}</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Informations du profil */}
              <motion.div className="profile-info" variants={itemVariants}>
                <motion.div 
                  className="profile-badge"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span className="badge-pulse"></span>
                  <span>{hero.badge || 'Disponible pour de nouveaux projets'}</span>
                </motion.div>

                <motion.h1 
                  className="profile-name"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {name}
                </motion.h1>
                
                {/* Composant Typing Effect */}
                <TypingEffect roles={roles} isLoading={isLoading} />

                <motion.div 
                  className="profile-location"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                >
                  <FiMapPin />
                  <span>{location}</span>
                </motion.div>
                
                <motion.p 
                  className="profile-intro"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  {shortBio}
                </motion.p>
                
                {/* Highlights */}
                <motion.div 
                  className="profile-highlights"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1 }}
                >
                  {highlights.map((highlight, index) => (
                    <motion.div 
                      key={index}
                      className="highlight"
                      whileHover={{ scale: 1.05, y: -5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className="highlight-icon">{getIcon(highlight.icon)}</div>
                      <div className="highlight-text">{highlight.label}</div>
                    </motion.div>
                  ))}
                </motion.div>
                
                {/* CTAs */}
                <motion.div 
                  className="profile-cta"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                >
                  <motion.button 
                    className="cta-primary"
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 40px rgba(3, 35, 107, 0.4)" }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => scrollToSection('#contact')}
                  >
                    <FiMail />
                    <span>Me contacter</span>
                  </motion.button>
                  
                  <Link to="/projets" className="cta-secondary-link">
                    <motion.div 
                      className="cta-secondary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Voir mes projets</span>
                      <FiArrowRight />
                    </motion.div>
                  </Link>
                  
                  <motion.button 
                    className="cta-outline"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => scrollToSection('#services')}
                  >
                    <span>Mes services</span>
                    <FiChevronDown />
                  </motion.button>
                </motion.div>

                {/* Social Links */}
                {Object.keys(socialLinks).length > 0 && (
                  <motion.div 
                    className="profile-socials"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.4 }}
                  >
                    {socialLinks.linkedin && (
                      <motion.a 
                        href={socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaLinkedin />
                      </motion.a>
                    )}
                    {socialLinks.twitter && (
                      <motion.a 
                        href={socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaTwitter />
                      </motion.a>
                    )}
                    {socialLinks.facebook && (
                      <motion.a 
                        href={socialLinks.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaFacebook />
                      </motion.a>
                    )}
                    {socialLinks.instagram && (
                      <motion.a 
                        href={socialLinks.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaInstagram />
                      </motion.a>
                    )}
                    {socialLinks.youtube && (
                      <motion.a 
                        href={socialLinks.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link"
                        whileHover={{ scale: 1.2, y: -3 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaYoutube />
                      </motion.a>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="scroll-hint"
          animate={{ y: [0, -10, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          onClick={() => scrollToSection('#about')}
        >
          <FiChevronDown />
          <span>Découvrir mon profil</span>
        </motion.div>
      </section>

      {/* ========== ABOUT / BIO SECTION ========== */}
      <section id="about" className="home-about">
        <div className="home-container">
          <motion.div 
            className="about-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div className="section-header">
              <span className="section-label">À propos</span>
              <h2 className="section-title">
                Mon <span className="title-accent">Histoire</span>
              </h2>
            </motion.div>
            
            <div className="about-layout">
              <motion.div 
                className="about-text"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {introduction.content ? (
  <div 
    className="about-paragraph"
    dangerouslySetInnerHTML={{ __html: cleanHtmlContent(introduction.content) }}
  />
                ) : (
                  <div className="about-paragraph">
                    <p>
                      Passionné de communication et d'engagement communautaire, je mets mes compétences 
                      au service de projets qui ont du sens. Mon parcours m'a permis de développer 
                      une expertise unique dans la création de contenus impactants.
                    </p>
                    <p>
                      Entre écriture, formation et leadership associatif, je construis des ponts 
                      entre les idées et leur réalisation concrète.
                    </p>
                  </div>
                )}
                <div className="about-signature">
                  <div className="signature-line"></div>
                  <span>— {firstName}</span>
                </div>
              </motion.div>
              
              <motion.div 
                className="about-stats"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {stats.map((stat, index) => (
                  <motion.div 
                    key={index} 
                    className="stat-card"
                    whileHover={{ scale: 1.05, y: -5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <motion.div 
                      className="stat-number"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.6 + index * 0.1, type: "spring" }}
                    >
                      {stat.number || stat.value}
                    </motion.div>
                    <div className="stat-label">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== SERVICES SECTION ========== */}
      {services.length > 0 && (
        <section id="services" className="home-services">
          <div className="home-container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Ce que je fais</span>
              <h2 className="section-title">
                Mes <span className="title-accent">Services</span>
              </h2>
              <p className="section-subtitle">
                Des services adaptés à vos besoins en communication et création de contenu.
              </p>
            </motion.div>
            
            <div className="services-grid">
              {services.map((service, index) => (
                <motion.div 
                  key={service.id || index}
                  className="service-card" style={{ background: '#1a1a2e' }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}
                >
                  <div className="service-icon">
                    {getServiceIcon(service.icon)}
                  </div>
                  <h3 className="service-title">{service.title}</h3>
                 <p className="service-description" style={{ color: '#E5E7EB' }}>{service.description}</p>
                  <div 
  className="service-number" 
  style={{ 
    background: 'none',
    WebkitBackgroundClip: 'unset',
    WebkitTextFillColor: 'unset',
    backgroundClip: 'unset',
    color: 'rgba(251, 191, 36, 0.35)'
  }}
>
  {String(index + 1).padStart(2, '0')}
</div>
                  <div className="service-glow"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== PROJECTS SECTION ========== */}
      {featuredProjects.length > 0 && (
        <section id="projects" className="home-projects">
          <div className="home-container">
            <motion.div 
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Portfolio</span>
              <h2 className="section-title">
                Projets <span className="title-accent">Récents</span>
              </h2>
              <p className="section-subtitle">
                Découvrez quelques-uns de mes projets récents et leurs impacts.
              </p>
            </motion.div>
            
            <div className="projects-grid">
              {featuredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="project-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  whileHover={{ y: -10 }}
                >
                  <Link to={`/projets/${project.slug}`} className="project-link">
                    <div className="project-image-wrapper">
                      {project.mainImage ? (
                        <img src={project.mainImage} alt={project.title} />
                      ) : (
                        <div className="project-image-placeholder">
                          <span>{project.title?.charAt(0) || 'P'}</span>
                        </div>
                      )}
                      <div className="project-overlay">
                        <div className="overlay-content">
                          <FiExternalLink />
                          <span>Voir le projet</span>
                        </div>
                      </div>
                    </div>
                    <div className="project-content">
                      <span className="project-category">
                        {project.categoryLabel || project.category || 'Projet'}
                      </span>
                      <h3 className="project-title">{project.title}</h3>
                      <p className="project-excerpt">
                        {project.shortDescription || project.description}
                      </p>
                      <div className="project-read-more">
                        <span>Découvrir</span>
                        <FiArrowRight />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
            
            <motion.div
              className="section-action"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/projets" className="view-all-link">
                <motion.div
                  className="cta-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Voir tous les projets</span>
                  <FiArrowRight />
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ========== SYPNASE / AGENCE SECTION ========== */}
      {sypnase.title && (
        <section className="home-sypnase">
          <div className="home-container">
            <motion.div
              className="sypnase-card"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="sypnase-bg">
                <div className="sypnase-gradient"></div>
                <div className="sypnase-pattern"></div>
                <div className="sypnase-particles">
                  <div className="s-particle s-particle-1"></div>
                  <div className="s-particle s-particle-2"></div>
                  <div className="s-particle s-particle-3"></div>
                </div>
              </div>
              
              <div className="sypnase-content">
                <div className="sypnase-text">
                  <span className="sypnase-label">{sypnase.badge || 'Mon Agence'}</span>
                  <h2 className="sypnase-title">{sypnase.title}</h2>
                  <p className="sypnase-slogan">{sypnase.slogan || 'Human + Tech Link'}</p>
                  <p className="sypnase-description">{sypnase.description}</p>
                  {sypnase.link && (
                    <motion.a 
                      href={sypnase.link} 
                      className="sypnase-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05, y: -3 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>Découvrir {sypnase.title}</span>
                      <FiExternalLink />
                    </motion.a>
                  )}
                </div>
                
                <div className="sypnase-visual">
                  <div className="sypnase-logo-wrapper">
                    <div className="logo-glow"></div>
                    <div className="logo-ring"></div>
                    <div className="sypnase-logo-container">
                      {sypnase.logo ? (
                        <img src={sypnase.logo} alt={sypnase.title} />
                      ) : (
                        <div className="sypnase-logo-placeholder">
                          <span>{sypnase.title?.charAt(0) || 'S'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ========== CONTACT / CTA SECTION ========== */}
      <section id="contact" className="home-contact">
        <div className="home-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label">Contact</span>
            <h2 className="section-title">
              Travaillons <span className="title-accent">Ensemble</span>
            </h2>
            <p className="section-subtitle">
              Vous avez un projet en tête ? N'hésitez pas à me contacter.
              Je serais ravi de discuter de la façon dont je peux vous aider.
            </p>
          </motion.div>
          
          <motion.div 
            className="contact-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="contact-icon">
              <FiMail />
            </div>
            
            <div className="contact-methods">
              {settings?.email && (
                <motion.a 
                  href={`mailto:${settings.email}`}
                  className="contact-method"
                  whileHover={{ x: 5 }}
                >
                  <div className="method-icon"><FiMail /></div>
                  <div className="method-details">
                    <span className="method-label">Email</span>
                    <span className="method-value">{settings.email}</span>
                  </div>
                </motion.a>
              )}
              
              {settings?.phones?.[0] && (
                <motion.a 
                  href={`tel:${settings.phones[0].replace(/\s/g, '')}`}
                  className="contact-method"
                  whileHover={{ x: 5 }}
                >
                  <div className="method-icon"><FiPhone /></div>
                  <div className="method-details">
                    <span className="method-label">Téléphone</span>
                    <span className="method-value">{settings.phones[0]}</span>
                  </div>
                </motion.a>
              )}
              
              <div className="contact-method">
                <div className="method-icon"><FiMapPin /></div>
                <div className="method-details">
                  <span className="method-label">Localisation</span>
                  <span className="method-value">{location}</span>
                </div>
              </div>
            </div>
            
            <div className="contact-cta-buttons">
              {(settings?.phones?.[0] || socialLinks?.whatsapp) && (
                <motion.a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-whatsapp"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaWhatsapp />
                  <span>Discuter sur WhatsApp</span>
                </motion.a>
              )}
              
              <Link to="/a-propos#contact" className="cta-contact-link">
                <motion.div
                  className="cta-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiSend />
                  <span>Envoyer un message</span>
                </motion.div>
              </Link>
            </div>
            
            <div className="contact-trust">
              <div className="trust-item">
                <FiCheck />
                <span>Réponse rapide</span>
              </div>
              <div className="trust-item">
                <FiCheck />
                <span>Devis gratuit</span>
              </div>
              <div className="trust-item">
                <FiCheck />
                <span>Accompagnement personnalisé</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;